import { Box, Chip, Tooltip, Typography } from '@mui/material';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import { AlgoliaHit } from 'instantsearch.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Base_Unit_Type_Enum, Contract_Status_Enum, Measurement_Unit_Enum } from '@kheops/graphql';
import { FormatMeasurementUnit, formatPriceByBillingType } from '@kheops/utils';
import FileUtils from '../../common/utils/file.utils';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import { ProductHit } from '../search';
import { ellipsisStyle, linkStyle } from '../../common/utils/style.utils';
import { formatPackagingPriceIncludingDescriptor } from '../../common/utils/common.utils';
import { extractSocialTagsFromDiscoveryHit } from '../../common/utils/social-tags.utils';
import SocialTags from '../../social-proof/SocialTags';
import { getProductUrlFromProductHit } from '../../discovery/discovery.utils';
import SearchUtils from '../../common/utils/search.utils';
import UpdateBasketButton from './UpdateBasketButton';

interface SearchProductRowProps {
  hit: AlgoliaHit<ProductHit>;
  contractStatus?: Contract_Status_Enum;
}

export default function SearchProductRow({ hit, contractStatus }: SearchProductRowProps): React.JSX.Element {
  const { t } = useTranslation(['products', 'contracts']);
  const currencyFormat = useCurrencyFormat();
  const packagingUrl = getProductUrlFromProductHit(hit);
  const socialTags = useMemo(() => extractSocialTagsFromDiscoveryHit(hit), [hit]);
  const priceComponent = useMemo(() => {
    const isContractSigned = contractStatus === Contract_Status_Enum.Signed;

    if (hit.baseUnitType === Base_Unit_Type_Enum.Bulk && !isContractSigned) {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    }

    return (
      <Tooltip
        title={t(isContractSigned ? 'products:price_per_packaging' : 'products:price_per_volume')}
      >
        <Typography variant="bodySmall">
          {isContractSigned
            ? formatPackagingPriceIncludingDescriptor(
              t,
              formatPriceByBillingType(currencyFormat.format(Number(hit.price)), hit.billingType),
              hit.packagingTradeItemUnitDescriptor,
            )
            : `${currencyFormat.format(Number(hit.volumePrice.value))}/${FormatMeasurementUnit(hit.volumePrice.unit as Measurement_Unit_Enum, 'fr', 1)}`}
        </Typography>
      </Tooltip>
    );
  }, [contractStatus, hit]);
  const imageSize = 72;

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        gap: 1.5,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', minWidth: 0, flex: 1 }}>
        <Box
          component={Link}
          to={packagingUrl}
          sx={{
            width: imageSize,
            height: imageSize,
            textAlign: 'center',
            borderRadius: '12px',
            overflow: 'hidden',
            mr: 1.5,
          }}
        >
          <Box
            component="img"
            src={FileUtils.BuildPackagingImageUrl({ photo: hit.photo, type: hit.family, size: { height: 160, fit: 'contain' } })}
            sx={{
              maxWidth: imageSize,
              maxHeight: imageSize,
            }}
          />
        </Box>
        <Box
          sx={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          {!!socialTags.length && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <SocialTags tags={socialTags} displayRules={{ rules: { 1: 1, 2: 2 } }} />
            </Box>
          )}
          <Box
            component={Link}
            to={packagingUrl}
            sx={{
              ...linkStyle,
            }}
          >
            <Typography variant="titleSmall" sx={{ ...ellipsisStyle }}>
              {hit.name}
            </Typography>
          </Box>
          <Typography
            variant="bodySmall"
            sx={{
              color: 'onSurfaceVariant.main',
            }}
          >
            {hit.baseUnit} - {hit.packaging}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, width: 232, mt: 1 }}>
        {!!hit.gtin && (
          <Chip
            variant="tag"
            icon={<DocumentScannerOutlinedIcon sx={{ transform: 'rotate(90deg)' }} />}
            label={hit.gtin}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', width: 248, gap: 0, justifyContent: 'end', height: '100%', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'end', gap: 0 }}>
          <Tooltip
            title={t(hit.baseUnitType === Base_Unit_Type_Enum.Bulk ? 'products:price_per_volume' : 'products:price_per_sales_unit')}
            placement="top"
          >
            <Typography variant="titleMedium">
              {SearchUtils.getFormatedUnitPriceFromAlgoliaHit(hit, currencyFormat.format)}
            </Typography>
          </Tooltip>
          {priceComponent}
        </Box>
        {contractStatus === Contract_Status_Enum.Signed && (
          <Box sx={{ display: 'flex', justifyContent: 'end', maxWidth: 132, pl: 1.5 }}>
            <UpdateBasketButton packagingId={hit.packaging_id} supplyingCompanyId={hit.company_id} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
